import {
  AuthUser,
  FacilityId,
  Gender,
  GradeKey,
  PaymentId,
  ProgramId,
  SubscriptionPlan,
  TimetableId,
  UserId,
} from '@gyym/sdk'

export type GoogleTagManagerId = `GTM-${string}`

export const GTM_ID = (process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID ?? '') as
  | GoogleTagManagerId
  | ''

export const GTM_PARAMS =
  process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_PARAMS ?? ''

export const existsGtmId = GTM_ID !== ''

export const push = (params: { [key: string]: unknown }) => {
  // Create and assign the queue when GTM has not loaded yet, so early events
  // are picked up by the snippet instead of being pushed to a throwaway array
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any
  const dataLayer = (w.dataLayer = w.dataLayer || [])

  // Log if not production
  if (process.env.NODE_ENV !== 'production') {
    console.log('[GTM]', params)
  }

  dataLayer.push(params)
}

type BaseAuthParams = {
  user_id: UserId
  age: number | null
  sex: Gender | null
  prefecture: string | null
  postal_code: string | null
}

const convertAuthUserToParams = (authUser: AuthUser): BaseAuthParams => ({
  user_id: authUser.id,
  age: authUser.age,
  sex: authUser.sex,
  prefecture: authUser.prefecture,
  postal_code: authUser.postalCode,
})

export const identify = (authUser: AuthUser) => {
  const authParams = convertAuthUserToParams(authUser)

  push({
    event: 'identify',
    identify: {
      ...authParams,
    },
  })
}

// Trigger PV
export const pageview = (
  path: string,
  authUser?: AuthUser,
  params?: {
    programId?: ProgramId
    facilityId?: FacilityId
  },
) => {
  const authParams = authUser
    ? convertAuthUserToParams(authUser)
    : {
        user_id: undefined,
        age: undefined,
        sex: undefined,
        prefecture: undefined,
        postal_code: undefined,
      }

  push({
    path,
    ...authParams,
    program_id: params?.programId,
    facility_id: params?.facilityId,
  })
}

// Trigger event
export const event = (event: string, params: { [key: string]: unknown }) => {
  push({
    event,
    ...params,
  })
}

type ReservationEventParams = {
  reservationId: number
  point: number
  programId: ProgramId
  facilityId: FacilityId
  timetableId: TimetableId
  referral:
    | 'facility'
    | 'program'
    | 'online_facility'
    | 'online_program'
    | 'favorites'
}

export const pushReservationEvent = (
  authUser: AuthUser,
  {
    reservationId,
    point,
    programId,
    facilityId,
    timetableId,
    referral,
  }: ReservationEventParams,
): void => {
  const params = convertAuthUserToParams(authUser)

  event('reserve', {
    ...params,
    eventID: `Reserve-${reservationId}`,
    point,
    program_id: programId,
    facility_id: facilityId,
    timetable_id: timetableId,
    referral,
  })
}

export const pushOpenPurchaseModalEvent = (
  authUser: AuthUser,
  {
    selectedPlanId,
    selectedPlanName,
    selectedBulkValue,
  }: {
    selectedPlanId?: string
    selectedPlanName?: string
    selectedBulkValue?: number
  },
) => {
  const params = convertAuthUserToParams(authUser)

  event('purchase_modal_open', {
    purchase_modal_open: {
      ...params,
      selectedPlanId,
      selectedPlanName,
      selectedBulkValue,
    },
  })
}

export const pushPurchasePointEvent = (
  authUser: AuthUser,
  paymentId: PaymentId,
  purchasePoint: number,
  purchasePrice: number,
): void => {
  const params = convertAuthUserToParams(authUser)

  event('purchase', {
    ...params,
    eventID: 'Purchase-' + paymentId,
    purchase_point: purchasePoint,
    purchase_price: purchasePrice,
  })
}

export const pushBeginSubscriptionEvent = (
  authUser: AuthUser,
  subscriptionId: string,
  plan: SubscriptionPlan,
  customerType: 'new' | 'repeat',
): void => {
  const params = convertAuthUserToParams(authUser)

  // Deprecated event
  event('purchase_subs', {
    ...params,
    plan_id: plan.id,
    plan_name: plan.name,
    customer_type: customerType,
    event: 'purchase_subs',
  })

  // New event
  event('begin_subscription', {
    ...params,
    subs_id: subscriptionId,
    plan_id: plan.id,
    plan_name: plan.name,
    customer_type: customerType,
    event: 'begin_subscription',
  })
}

export const pushCancelSubscriptionEvent = (
  authUser: AuthUser,
  subscriptionId: string,
  plan: SubscriptionPlan,
): void => {
  const params = convertAuthUserToParams(authUser)

  event('cancel_subscription', {
    ...params,
    subs_id: subscriptionId,
    plan_id: plan.id,
    plan_name: plan.name,
  })
}

export const pushSubscriptionCancelSurveyEvent = (
  authUser: AuthUser,
  values: { reasons: string[]; intentToContinue: string; otherReason?: string },
): void => {
  const params = convertAuthUserToParams(authUser)

  push({
    event: 'subscription_cancel_survey',
    subscription_cancel_survey: {
      ...params,
      reasons: values.reasons,
      intent_to_continue: values.intentToContinue,
      other_reason: values.otherReason ?? '',
    },
  })
}

/**
 * 法人会員サービスLP (/corp) の紹介資料請求フォーム送信完了。
 * 未認証フォームのため、個人情報 (会社名・氏名・メールアドレス等) は送らない。
 */
export const pushIntroductionMaterialRequestEvent = (
  employeeCount: string,
): void => {
  event('introduction_material_request', {
    employee_count: employeeCount,
  })
}

export type CorpLpBannerPlacement =
  | 'side_menu'
  | 'pricing_bottom'
  | 'search_bottom'

/**
 * 法人会員サービスLP (/corp) への導線バナーのクリック。
 * サイト内リンクに utm_* を付けると流入元 (GA のセッション・会員登録時に送る utm) を
 * 上書きしてしまうため、設置場所はこのイベントのパラメータで区別する。
 */
export const pushCorpLpBannerClickEvent = (
  placement: CorpLpBannerPlacement,
): void => {
  event('corp_lp_banner_click', { placement })
}

export const pushSignupEvent = (authUser: AuthUser): void => {
  const params = convertAuthUserToParams(authUser)

  event('register', {
    ...params,
    eventID: `CompleteRegistration-${params.user_id}`,
  })
}

export const pushLoginEvent = (authUser: AuthUser): void => {
  const params = convertAuthUserToParams(authUser)

  event('login', {
    ...params,
  })
}

export const pushModalRankUpEvent = (
  authUser: AuthUser,
  gradeKey: GradeKey,
) => {
  const params = convertAuthUserToParams(authUser)

  event('modal_rank_up', {
    ...params,
    grade_key: gradeKey,
  })
}

export const pushModalRankDownEvent = (
  authUser: AuthUser,
  gradeKey: GradeKey,
) => {
  const params = convertAuthUserToParams(authUser)

  event('modal_rank_down', {
    ...params,
    grade_key: gradeKey,
  })
}

export const pushModalCompleteQuestEvent = (
  authUser: AuthUser,
  gradeKey: GradeKey,
) => {
  const params = convertAuthUserToParams(authUser)

  event('modal_complete_quest', {
    ...params,
    grade_key: gradeKey,
  })
}

export const pushModalWelcomeBackEvent = (
  authUser: AuthUser,
  gradeKey: GradeKey,
) => {
  const params = convertAuthUserToParams(authUser)

  event('modal_welcome_back', {
    ...params,
    grade_key: gradeKey,
  })
}
